import { SelectionField } from '@amzn/cuminuicomponents';
import { KatDateRangePicker, KatDivider, KatIcon } from '@amzn/katal-react';
import { isAfter, isBefore } from 'date-fns';
import { useRouter } from 'next/router';
import qs from 'qs';
import React, { useEffect, useRef, useState } from 'react';
import { ButtonTypes, MetricValues } from 'src/constants/metrics';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { fetchSearchResults, SearchType, setInputValue } from 'src/redux/slices/postSearch';
import { AppState } from 'src/redux/store';
import { AggregationBucket, RequestType, SearchDataRequestType } from 'src/types/Search';
import { ProfileStatus } from 'src/types/UserInfo';
import {
  getConfigValue, updateConfig, useDebounce, useFirstRender, useMetrics, useTranslation,
} from 'src/utils';
import LocalStorageKeys from 'src/utils/localStorageKeys';
import { selectUserPreferences, updateUserPreferences } from 'src/redux/slices/userInfo';
import getDateRangeDates from 'src/utils/getDateRangeDates';
import DropdownWithPreference from '../DropdownWithDefault';
import { createSearchAPIContract } from './utils/createSearchAPIContract';
import {
  getAmazonReplied,
  getFiltersFromURL,
  getHasReplies,
  getHasSolution,
  getQueryForUserId,
} from './utils/getFiltersStateFromURL';
import {
  DateRangeType, FilterLabels, FilterName, SelectedFiltersStateType, SortByType,
} from './utils/types';

const searchStateToUrl = (searchState: SelectedFiltersStateType) => {
  // filter out values of empty strings
  const cleanSearchState = Object.entries(searchState).reduce((acc: any, [key, val]) => {
    if (val) {
      return {
        ...acc,
        [key]: val,
      };
    }
    return acc;
  }, {});
  const { dateRange, startDate, endDate } = cleanSearchState;
  return (
    `${window.location.origin}${window.location.pathname}?${qs.stringify(
      {
        ...cleanSearchState,
        dateRange,
        startDate: dateRange === DateRangeType.custom ? startDate : undefined,
        endDate: dateRange === DateRangeType.custom ? endDate : undefined,
      },
      {
        encodeValuesOnly: true,
        arrayFormat: 'comma',
      },
    )}`
  );
};

const getSelectedFiltersInitState = (
  isRegistered: boolean,
  defaultSortBy: SortByType,
  allowedSortByTypes: string[],
  userId?: string,
  searchTerm?: string,
) => {
  const localStorageSortBy: SortByType | undefined = getConfigValue(
    LocalStorageKeys.discussionListingSortBy,
    (sortBy) => !!sortBy && allowedSortByTypes.includes(sortBy),
    userId,
  );
  const localStorageDateRange: DateRangeType | undefined = getConfigValue(
    LocalStorageKeys.discussionListingDateRange,
    (dateRange) => !!dateRange && (Object.values(DateRangeType).includes(dateRange)),
    userId,
  );
  const dateRange = (isRegistered && localStorageDateRange) || DateRangeType.threeMonths;
  const { startDate, endDate } = getDateRangeDates(dateRange);
  const sortBy: SortByType = isRegistered
    ? localStorageSortBy || SortByType.relevance
    : defaultSortBy;

  return {
    [FilterName.sortBy]: sortBy,
    [FilterName.dateRange]: dateRange,
    // TODO: connect dateRange attribute to startDate and endDate
    // for automatically setting the initial values
    [FilterName.startDate]: startDate,
    [FilterName.endDate]: endDate,
    [FilterName.quickFilters]: [],
    [FilterName.replies]: FilterLabels.repliesAll,
    [FilterName.categories]: [],
    [FilterName.tags]: [],
    [FilterName.groups]: [],
    [FilterName.searchTerm]: searchTerm || '',
  };
};

export interface RenderFiltersBucketStateType {
  [FilterName.quickFilters]: Array<AggregationBucket>;
  [FilterName.replies]: Array<AggregationBucket>;
  [FilterName.categories]: Array<AggregationBucket>;
  [FilterName.tags]: Array<AggregationBucket>;
  [FilterName.groups]: Array<AggregationBucket>;
}

export interface RenderFiltersStateType {
  [FilterName.quickFilters]: Array<{ label: string; value: string }>;
  [FilterName.replies]: Array<{ label: string; value: string }>;
  [FilterName.categories]: Array<{ label: string; value: string }>;
  [FilterName.tags]: Array<{ label: string; value: string }>;
  [FilterName.groups]: Array<{ label: string; value: string }>;
}

export const getSortOptions = (
  isRegistered: boolean,
) => {
  const { t } = useTranslation();
  const sortOptions = [
    {
      name: `${t('sort_latestActivity')}`,
      value: SortByType.lastActivityTime,
    },
    {
      name: `${t('sort_recentlyCreated')}`,
      value: SortByType.createdAt,
    },
    {
      name: `${t('sort_mostViewed')}`,
      value: SortByType.totalViews,
    },
    {
      name: `${t('sort_mostVoted')}`,
      value: SortByType.totalVotes,
    },
  ];

  if (isRegistered) {
    sortOptions.unshift(
      {
        name: `${t('sort_relevance')}`,
        value: SortByType.relevance,
      },
    );
  }

  return sortOptions;
};

const SearchListingsSideBar = () => {
  const { replace, query } = useRouter();
  const { groupId } = query as { groupId: string };
  const firstRender = useFirstRender();
  const [shouldFetchAggregations, setShouldFetchAggregations] = useState(true);
  const aggregations = useAppSelector(({ postSearch }) => postSearch.threadListing.aggregations);
  const threadListingRefresh = useAppSelector(
    ({ postSearch }) => postSearch.threadListing.refresh,
  );
  const nonDebouncedSearchTerm = useAppSelector(
    ({ postSearch }) => postSearch.searchBar.inputValue,
  );
  const searchTerm = useDebounce(nonDebouncedSearchTerm, 300) as string;
  const userId = useAppSelector((state: AppState) => state.userInfo.userId);
  const categoryIdsObject = useAppSelector((state: AppState) => state.threadCategories.categories);
  const tagIdsObject = useAppSelector((state: AppState) => state.threadCategories.tags);
  const profileStatus = useAppSelector(({ userInfo }) => userInfo.profileStatus);
  const isRegistered = profileStatus === ProfileStatus.registered;
  const defaultSortBy: SortByType = SortByType.lastActivityTime;
  const allowedSortByTypes = Object.values(SortByType);
  const { clickMetric } = useMetrics();
  const userPreferences = useAppSelector(selectUserPreferences);
  const userPreferenceStatus = useAppSelector(({ userInfo }) => userInfo.userPreferenceStatus);

  const getFiltersDefaultValues = () => {
    const defaultValues = getSelectedFiltersInitState(
      isRegistered,
      defaultSortBy,
      allowedSortByTypes,
      userId,
      searchTerm,
    );
    defaultValues[FilterName.sortBy] = userPreferences.threadListingSortBy;
    defaultValues[FilterName.dateRange] = userPreferences.threadListingDateRange;
    return defaultValues;
  };

  const getFilterInitStateFromUrl = () => {
    const filtersFromURL = getFiltersFromURL({
      categoriesList: Object.keys(categoryIdsObject),
      tagsList: Object.keys(tagIdsObject),
      profileStatus,
    });
    const {
      sortBy,
      dateRange,
      quickFilters,
      replies,
      categories,
      tags,
      searchTerm: urlSearchTerm,
      groups,
      startDate,
      endDate,
    } = getFiltersDefaultValues();
    const dateRangeState = filtersFromURL.dateRange || dateRange;
    const stateFromURL: SelectedFiltersStateType = {
      sortBy: filtersFromURL.sortBy || sortBy,
      dateRange: dateRangeState,
      quickFilters: filtersFromURL.quickFilters || quickFilters,
      replies: filtersFromURL.replies || replies,
      categories: filtersFromURL.categories || categories,
      tags: filtersFromURL.tags || tags,
      searchTerm: filtersFromURL.searchTerm || urlSearchTerm,
      groups,
    };
    if (filtersFromURL.dateRange === DateRangeType.custom) {
      stateFromURL.startDate = filtersFromURL.startDate || startDate;
      stateFromURL.endDate = filtersFromURL.endDate || endDate;
    } else if (filtersFromURL.dateRange === DateRangeType.allTime) {
      stateFromURL.startDate = undefined;
      stateFromURL.endDate = undefined;
    } else {
      const dateRangeDatesState = getDateRangeDates(dateRangeState);
      stateFromURL.startDate = dateRangeDatesState.startDate;
      stateFromURL.endDate = dateRangeDatesState.endDate;
    }
    return stateFromURL;
  };
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersStateType>(
    getFilterInitStateFromUrl(),
  );
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const sortChangeHandler = (updatedValue: SortByType) => {
    clickMetric({ metricValue: updatedValue, metricName: ButtonTypes.discussionsFilter });
    return setSelectedFilters((oldValues) => ({
      ...oldValues,
      [FilterName.sortBy]: updatedValue,
    }));
  };

  const dateRangeChangeHandler = (
    updatedValue: DateRangeType,
  ) => {
    clickMetric({ metricValue: updatedValue, metricName: ButtonTypes.discussionsFilter });
    const { startDate, endDate } = getDateRangeDates(updatedValue);
    return setSelectedFilters((oldValues) => ({
      ...oldValues,
      [FilterName.dateRange]: updatedValue,
      [FilterName.startDate]: startDate,
      [FilterName.endDate]: endDate,
    }));
  };

  const dateRangePickerChangeHandler = (event: KatDateRangePicker.ChangeEvent) => {
    const { detail: { startDate, endDate } } = event;
    return setSelectedFilters((oldValues) => ({
      ...oldValues,
      [FilterName.startDate]: new Date(startDate).getTime(),
      [FilterName.endDate]: new Date(endDate).getTime(),
    }));
  };

  const setArrayValues = (value: string, existingValues: string[]) => (
    existingValues.includes(value)
      ? existingValues.filter((existingValue) => existingValue !== value)
      : [...existingValues, value]);

  const categoriesChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;
    clickMetric({ metricValue: value, metricName: ButtonTypes.discussionsFilter });
    setShouldFetchAggregations(false);
    return setSelectedFilters((oldValues) => {
      const selectedCategories = oldValues[FilterName.categories];
      const selectedTags = oldValues[FilterName.tags];
      const updatedCategories = setArrayValues(value, selectedCategories);

      // removing previously selected tags when category is removed
      const updatedTags = !updatedCategories.includes(value)
        ? selectedTags.filter((tagId) => !categoryIdsObject[value].tags.includes(tagId))
        : selectedTags;
      return {
        ...oldValues,
        [FilterName.categories]: updatedCategories,
        [FilterName.tags]: updatedTags,
      };
    });
  };

  const tagsChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;
    clickMetric({ metricValue: value, metricName: ButtonTypes.discussionsFilter });
    setShouldFetchAggregations(false);
    return setSelectedFilters((oldValues) => {
      const selectedTags = oldValues[FilterName.tags];
      const updatedTags = setArrayValues(value, selectedTags);
      return {
        ...oldValues,
        [FilterName.tags]: updatedTags,
      };
    });
  };

  const quickFilterChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;
    clickMetric({ metricValue: value, metricName: ButtonTypes.discussionsFilter });
    setShouldFetchAggregations(false);
    return setSelectedFilters((oldValues) => {
      const selectedQuickFilters = oldValues[FilterName.quickFilters];
      const updatedQuickFilters = setArrayValues(value, selectedQuickFilters);
      return {
        ...oldValues,
        [FilterName.quickFilters]: updatedQuickFilters,
      };
    });
  };

  const repliesChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;
    clickMetric({ metricValue: value, metricName: ButtonTypes.discussionsFilter });
    return setSelectedFilters((oldValues) => ({
      ...oldValues,
      [FilterName.replies]: value,
    }));
  };

  const getPinFilters = (categories: string[], groups: string[], isPinned: boolean) => {
    if (categories.length !== 0) {
      return { pinnedToCategory: isPinned };
    } if (groups.length !== 0) {
      return { pinnedToGroup: isPinned };
    }
    return { pinnedToListing: isPinned };
  };

  const searchRequestHandler = () => {
    // workflow to encode state in URL and make filter API call
    const href = searchStateToUrl({ ...selectedFilters, searchTerm });
    replace(href, href, { shallow: true });

    const {
      sortBy, startDate, endDate, categories, tags, groups, quickFilters, replies,
    } = selectedFilters;
    const searchTermString = searchTerm.trim();
    const requestType = searchTermString ? RequestType.search : RequestType.listing;
    const pinFilters = requestType === RequestType.listing
      ? getPinFilters(categories, groupId ? [groupId] : groups, false) : {};
    const searchDataRequestType = requestType === RequestType.search
      ? SearchDataRequestType.SEARCH_DATA : undefined;

    const getRequestPayload = (type: RequestType) => ({
      requestType: type,
      type: SearchType.threadListing,
      request: createSearchAPIContract({
        sortBy,
        startDate,
        endDate,
        categoryIdList: categories,
        tagIdList: tags,
        groupIdList: groupId ? [groupId] : groups,
        searchTerm: type === RequestType.search ? searchTermString : undefined,
        searchDataRequestType,
        hasReplies: getHasReplies(replies),
        hasSolution: getHasSolution(quickFilters),
        amazonReplied: getAmazonReplied(quickFilters),
        userId: getQueryForUserId({ quickFilters, userId: userId as string }),
        requestType: type,
        fetchAggregations: shouldFetchAggregations,
        ...pinFilters, // Note: "Recommended" request type does not respect *.pinnedTo filters.
      }),
    });

    if (requestType === RequestType.listing
      || (
        requestType === RequestType.search
        && searchTermString.length > 2
      )
    ) {
      dispatch(fetchSearchResults(getRequestPayload(requestType)));
    } else if (firstRender) {
      dispatch(fetchSearchResults(getRequestPayload(RequestType.listing)));
    }
    setShouldFetchAggregations(true);
  };

  useEffect(() => {
    if (threadListingRefresh) {
      searchRequestHandler();
    }
  }, [threadListingRefresh]);
  useEffect(() => {
    searchRequestHandler();
  }, [
    searchTerm,
    selectedFilters[FilterName.sortBy],
    selectedFilters[FilterName.dateRange],
    selectedFilters[FilterName.startDate],
    selectedFilters[FilterName.endDate],
    selectedFilters[FilterName.quickFilters],
    selectedFilters[FilterName.replies],
    selectedFilters[FilterName.categories],
    selectedFilters[FilterName.tags],
    selectedFilters[FilterName.groups],
    groupId,
  ]);

  useEffect(() => {
    const {
      categories, groups,
    } = selectedFilters;

    dispatch(fetchSearchResults({
      requestType: RequestType.listing,
      type: SearchType.pinnedThreads,
      request: createSearchAPIContract({
        requestType: RequestType.listing,
        ...getPinFilters(categories, groupId ? [groupId] : groups, true),
        categoryIdList: categories,
        groupIdList: groupId ? [groupId] : groups,
        sortBy: SortByType.createdAt,
      }),
    }));
  }, [
    selectedFilters[FilterName.categories],
    selectedFilters[FilterName.groups],
    groupId,
  ]);

  useEffect(() => {
    // workflow to load state from URL

    dispatch(setInputValue({
      type: SearchType.searchBar,
      // set searchbar value from URL or from redux state
      inputValue: selectedFilters.searchTerm,
    }));
  }, []);

  const dateRangePickerRef = useRef<KatDateRangePicker.Element>(null);
  const { dateRange, startDate, endDate } = selectedFilters;
  const { current } = dateRangePickerRef;
  useEffect(() => {
    if (current && dateRange === DateRangeType.custom) {
      const { startPicker, endPicker } = current;
      if (startPicker && endPicker) {
        startPicker.isDateDisabled = (date) => {
          if (endDate) {
            const endDateObj = new Date(endDate);
            return isAfter(date, endDateObj);
          }
          return false;
        };

        endPicker.isDateDisabled = (date) => {
          if (startDate) {
            const startDateObj = new Date(startDate);
            return isBefore(date, startDateObj);
          }
          return false;
        };
      }
    }
  }, [!current, current?.startPicker, current?.endPicker, dateRange, startDate, endDate]);

  const startValue = startDate ? new Date(startDate) : undefined;
  const endValue = endDate ? new Date(endDate) : undefined;

  const quickFiltersOptions = [
    { label: t('quickFilters_MostHelpfulReplies'), value: FilterLabels.hasSolution },
    { label: t('quickFilters_AmazonResponded'), value: FilterLabels.hasAmazonReply },
  ];
  if (isRegistered) {
    quickFiltersOptions.push({ label: t('quickFilters_MyDiscussions'), value: FilterLabels.userId });
  }

  const renderTags = () => {
    const options = selectedFilters.categories.reduce((prev, categoryId) => {
      const tagOptions = categoryIdsObject[categoryId].tags
        .filter((tagId) => aggregations.tags?.[tagId])
        .map((tagId) => {
          const { stringId } = tagIdsObject[tagId];
          const count = aggregations.tags[tagId];
          return ({
            label: t(stringId),
            value: tagId,
            count,
          });
        });
      return [...prev, ...tagOptions];
    }, [] as Array<{ label: string, value: string, count: number }>)
      .sort((a, b) => (b.count - a.count));

    return (
      <>
        <span className="mb-1.5 text-sm font-normal">{t('filters_header_tags')}</span>
        <div className="mt-1.5 mb-5">
          <SelectionField
            id={FilterName.tags}
            options={options}
            value={selectedFilters.tags}
            onChange={tagsChangeHandler}
          />
          {selectedFilters.categories.length > 0 && options.length === 0 && <div className="text-kat-tarpon">{t('tags_empty')}</div>}
          {selectedFilters.categories.length === 0 && <div className="text-kat-tarpon">{t('tags_populate')}</div>}
        </div>
      </>
    );
  };

  const dateRangeOptions = [
    {
      name: `${t('filters_dateTime_AllTime')}`,
      value: DateRangeType.allTime,
    },
    {
      name: `${t('filters_dateTime_pastDay')}`,
      value: DateRangeType.pastDay,
    },
    {
      name: `${t('filters_dateTime_pastWeek')}`,
      value: DateRangeType.pastWeek,
    },
    {
      name: `${t('filters_dateTime_pastMonth')}`,
      value: DateRangeType.pastMonth,
    },
    {
      name: `${t('filters_dateTime_threeMonth')}`,
      value: DateRangeType.threeMonths,
    },
    {
      name: `${t('filters_dateTime_pastYear')}`,
      value: DateRangeType.pastYear,
    },
    {
      name: `${t('filters_dateTime_custom')}`,
      value: DateRangeType.custom,
      disableDefault: true,
      constraintLabel: t('preferenceDropdown_daterange_constraintLabel'),
    },
  ];

  const clearFiltersHandler = () => {
    clickMetric({
      metricValue: MetricValues.clear,
      metricName: ButtonTypes.discussionsFilter,
    });
    return setSelectedFilters({
      ...getFiltersDefaultValues(),
      [FilterName.sortBy]: selectedFilters[FilterName.sortBy],
    });
  };

  return (
    <div>
      <div className="flex flex-auto flex-col ltr:pr-3 rtl:pl-3">
        <div className="mb-1 flex flex-row content-center">
          <KatIcon size="small" name="sort" className="ltr:mr-1 rtl:ml-1" />
          <h5 className="text-sm font-bold">{t('sort_header')}</h5>
        </div>

        <DropdownWithPreference<SortByType>
          dataTestId="sortby-dropdown"
          options={getSortOptions(isRegistered)}
          onChange={sortChangeHandler}
          value={selectedFilters[FilterName.sortBy]}
          modalTitle={t('preferenceDropdown_sortBy_modal_title')}
          modalContent={t('preferenceDropdown_sortBy_modal_content')}
          defaultSelectedOption={
            getFiltersDefaultValues().sortBy
          }
          onSaveDefault={(value) => {
            updateConfig(
              LocalStorageKeys.discussionListingSortBy,
              value,
              userId,
            );
            dispatch(updateUserPreferences({
              sortByPref: value,
            }));
          }}
          allowDefault={isRegistered}
          modalFetchStatus={userPreferenceStatus}
          modalFailureMessage={t('request_genericFailureHeader')}
        />

        <KatDivider variant="athens" className="my-3.5 bg-kat-seattle" />

        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row">
            <KatIcon size="small" name="filter_list" className="ltr:mr-1 rtl:ml-1" />
            <h5 className="text-sm font-bold">{t('filters_container_header')}</h5>
          </div>
          <button
            type="button"
            data-testid="button-clear-all"
            onClick={clearFiltersHandler}
            className="flex cursor-pointer items-start border-none bg-kat-athens/0 px-3 text-xs font-normal text-kat-eastern outline-none focus:outline-none"
          >
            <span>
              {t('filters_clearAll')}
              <KatIcon name="highlight_remove" size="tiny" className="ltr:ml-1 rtl:mr-1" />
            </span>
          </button>
        </div>

        <div className="mt-4 mb-5">
          <div className="space-y-1">
            <span className="text-sm font-normal">{t('filters_header_dateTime')}</span>
            <DropdownWithPreference<DateRangeType>
              options={dateRangeOptions}
              dataTestId="daterange-dropdown"
              onChange={dateRangeChangeHandler}
              value={selectedFilters[FilterName.dateRange]}
              modalTitle={t('preferenceDropdown_daterange_modal_title')}
              modalContent={t('preferenceDropdown_daterange_modal_content')}
              defaultSelectedOption={
                getFiltersDefaultValues().dateRange
              }
              onSaveDefault={(value) => {
                updateConfig(
                  LocalStorageKeys.discussionListingDateRange,
                  value,
                  userId,
                );
                dispatch(updateUserPreferences({ dateRangePref: value }));
              }}
              allowDefault={isRegistered}
              modalFetchStatus={userPreferenceStatus}
              modalFailureMessage={t('request_genericFailureHeader')}
            />
          </div>
          {selectedFilters[FilterName.dateRange] === DateRangeType.custom && (
            <div className="col-span-3 mt-3.5">
              <KatDateRangePicker
                ref={dateRangePickerRef}
                startValue={startValue}
                endValue={endValue}
                onChange={dateRangePickerChangeHandler}
                data-testid="date-range-picker"
                locale="en-US"
                endLabel=""
                startLabel=""
              />
            </div>
          )}

        </div>

        <span className="mb-1.5 text-sm font-normal">{t('filters_header')}</span>
        <div className="mt-1.5 mb-5">
          <SelectionField
            id={FilterName.quickFilters}
            options={quickFiltersOptions}
            value={selectedFilters.quickFilters}
            onChange={quickFilterChangeHandler}
            className="2xl:grid 2xl:grid-cols-2"
          />
        </div>

        <span className="mb-1.5 text-sm font-normal">{t('filters_header_discussions')}</span>
        <div className="mt-1.5 mb-5">
          <SelectionField
            id={FilterName.replies}
            options={[
              { label: t('replies_All'), value: FilterLabels.repliesAll },
              { label: t('replies_hasReplies'), value: FilterLabels.hasReplies },
              { label: t('replies_hasNoReplies'), value: FilterLabels.hasNoReplies },
            ]}
            value={selectedFilters.replies}
            onChange={repliesChangeHandler}
          />
        </div>

        <span className="mb-1.5 text-sm font-normal">{t('filters_header_categories')}</span>
        <div className="mt-1.5 mb-5">
          <SelectionField
            id={FilterName.categories}
            options={Object.values(categoryIdsObject).map(({ id, nameStringId }) => {
              const count = aggregations.categories?.[id] || 0;
              return ({
                label: t(nameStringId),
                value: id,
                count,
              });
            }).sort((a, b) => (b.count - a.count))}
            value={selectedFilters.categories}
            onChange={categoriesChangeHandler}
          />
        </div>

        {renderTags()}
        <KatDivider variant="athens" className="bg-kat-seattle" />
      </div>
    </div>
  );
};

export default SearchListingsSideBar;
